import styled from "styled-components";

// =====================================================
// MODERN CARD ITEM STYLES
// Clean, modern card design with subtle shadows
// =====================================================

export const ContCard = styled.div`
  width: 100%;
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
  position: relative;
  display: flex;
  flex-direction: column;

  &:hover {
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }
`;

export const ContImgCard = styled.div.attrs(props => ({
  url: props.url || '',
  bgc: props.load ? '#f3f4f6' : '#fafafa'
}))`
  background-image: ${props => `url(${props.url})`};
  background-position: center;
  background-size: contain;
  background-repeat: no-repeat;
  background-color: ${props => props.bgc};
  width: 100%;
  aspect-ratio: 4/3;
  position: relative;
  border-bottom: 1px solid #f3f4f6;
  display: flex;
  justify-content: center;
  align-items: center;
`;

export const BtnOptionsCard = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 32px;
  height: 32px;
  background: white;
  border-radius: 8px;
  border: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  color: #6b7280;
  font-size: 14px;
  cursor: pointer;
  z-index: 2;
  transition: all 0.2s ease;

  &:hover {
    background: #7367f0;
    color: white;
  }
`;

export const BtnNextImg = styled.button.attrs(props => ({
  left: props.l || '12px',
  right: props.r || 'auto'
}))`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  left: ${props => props.left};
  right: ${props => props.right};
  display: flex;
  justify-content: center;
  align-items: center;
  width: 32px;
  height: 32px;
  background: white;
  border-radius: 50%;
  border: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  color: #6b7280;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #7367f0;
    color: white;
  }
`;

export const ContDataInfoOne = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  gap: 8px;
`;

export const ContDataInfoTitle = styled.div`
  padding: 0 16px 12px;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
  color: #111827;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word; /* Essential for long strings */
  height: 42px; /* Fixed height instead of min-height to force clamp */
  min-height: 42px;
  font-family: "Inter", -apple-system, BlinkMacSystemFont, sans-serif;
`;

export const CardMeta = styled.div`
  padding: 0 16px 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #6b7280;

  .label {
    font-weight: 600;
    color: #374151;
    min-width: 60px;
  }

  .value {
    color: #6b7280;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const BtnSelectCard = styled.button.attrs(props => ({
  bgc: props.active ? "#7367F0" : "#f3f4f6"
}))`
  width: 100%;
  background: ${props => props.bgc};
  height: 44px;
  border: none;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 13px;
  font-weight: 600;
  font-family: "Inter", -apple-system, BlinkMacSystemFont, sans-serif;
  color: ${props => props.active ? 'white' : '#374151'};
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: auto;

  &:hover {
    background: ${props => props.active ? '#5a4fcf' : '#e5e7eb'};
  }
`;

export const ContIconsInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  position: absolute;
  left: 12px;
  top: 12px;
  z-index: 2;
`;

export const ContIconInfo = styled.span.attrs(props => ({
  mr: props.mr || '0',
  ml: props.ml || '0'
}))`
  width: 28px;
  height: 28px;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  border-radius: 6px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: white;
  font-size: 12px;
`;

// Status Badge
export const StatusBadge = styled.span.attrs(props => ({
  variant: props.variant || 'default'
}))`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  font-family: "Inter", -apple-system, BlinkMacSystemFont, sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  ${props => {
    switch (props.variant) {
      case 'success':
        return `background: #dcfce7; color: #166534;`;
      case 'warning':
        return `background: #fef3c7; color: #92400e;`;
      case 'danger':
        return `background: #fee2e2; color: #991b1b;`;
      case 'info':
        return `background: #dbeafe; color: #1e40af;`;
      default:
        return `background: #f3f4f6; color: #374151;`;
    }
  }}
`;

export const ContMenuCard = styled.div.attrs(props => ({
  translateX: props.active ? "translateX(0)" : "translateX(100%)"
}))`
  position: absolute;
  inset: 0;
  background: white;
  z-index: 10;
  transition: transform 0.25s ease;
  transform: ${props => props.translateX};
  display: flex;
  flex-direction: column;
`;

export const BtnCloseMenu = styled.button`
  position: absolute;
  top: 12px;
  left: 12px;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: #7367f0;
  color: white;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: #5a4fcf;
  }
`;

export const ContTitleMenu = styled.div`
  padding: 16px;
  padding-left: 56px;
  border-bottom: 1px solid #f3f4f6;
  font-weight: 600;
  font-size: 14px;
  color: #111827;
`;

export const ListOptions = styled.ul`
  flex: 1;
  list-style: none;
  padding: 8px 0;
  margin: 0;
  overflow-y: auto;
`;

export const OptionList = styled.li`
  padding: 12px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  font-size: 14px;
  color: #374151;
  transition: background 0.15s ease;

  &:hover {
    background: #f8f7ff;
    color: #7367f0;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

// Loading States
export const ContImgLoad = styled.div`
  width: 100%;
  aspect-ratio: 4/3;
  background: linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 50%, #f3f4f6 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;

  @keyframes shimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }
`;

export const ContLabelLoad = styled.div.attrs(props => ({
  w: props.w || '100%'
}))`
  width: ${props => props.w};
  height: 16px;
  border-radius: 8px;
  background: linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 50%, #f3f4f6 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;

  @keyframes shimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }
`;
